import _get from 'lodash/get';
import axios, { AxiosError, AxiosInstance } from 'axios';
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { GraphQLError } from 'graphql';
import { GraphQLErrors, NetworkError } from '@apollo/client/errors';
import {
	ApolloClient,
	InMemoryCache,
	ApolloProvider,
	NormalizedCacheObject,
	createHttpLink,
	ApolloLink,
	from,
	fromPromise,
	defaultDataIdFromObject,
	ServerError,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { AppDomain, Errors, RequestErrors } from 'src/types';
import { AuthTokens, AUTH_PATHS, useAuthRequest, useAuthToken } from 'src/hooks/useAuthRequest';

export interface DataContextEmittedValues {
	apiVersion?: number;
	domain: string;
	domains: Record<string, AppDomain>;
	gql?: ApolloClient<NormalizedCacheObject>;
	restClient?: AxiosInstance;
	getClient?: (domain: string) => ApolloClient<NormalizedCacheObject>;
}

export const DataContext = createContext<DataContextEmittedValues>({
	domain: 'wppa',
	domains: {},
});

export interface Props {
	children: React.ReactChild;
	apiVersion?: number;
	appContext?: string;
	domain: string;
	authDomain?: string;
	authPaths?: Record<string, string>;
	domains: Record<string, AppDomain>;
	refreshToken?: {
		override?: () => Promise<AuthTokens>;
		headers?: Record<string, string>;
	};
	onContinueSession: (tokens?: AuthTokens) => void;
	onInvalidAccess: () => void;
	onErrors?: (errors: RequestErrors) => void;
}

function getRefereerHost() {
	return window.location.host;
}

/***
 * detect invalid token
 */
interface GQLDetectAuthInvalid {
	graphQLErrors?: GraphQLErrors;
	networkError?: NetworkError;
}
function gqlDetectAuthInvalid(err: GQLDetectAuthInvalid) {
	const { graphQLErrors, networkError } = err;
	if (networkError && (networkError as ServerError).statusCode === 401) return true;
	// return err.extensions.code === 'UNAUTHORIZED_ACCESS';
}

function restDetectAuthInvalid(error: any) {
	return error.response.status === 401;
}

export function decorateHeaders(headers: Record<string, string>) {
	return {
		...headers,
		'url-referer': window.location.href,
	};
}

const clientList: Record<string, ApolloClient<any>> = {};

export const DataProvider = (props: Props) => {
	const { apiVersion = 1, appContext, onErrors } = props;
	const createGraphQLClient = useCallback((domain: string) => {
		const { base, xAPIKey: defaultXAPIKey } = domains[domain];
		// graphql
		const httpLink = createHttpLink({
			uri: `${base}graphql`,
		});

		/***
		 * decorate with app headers:
		 * X-Application-Context => identify if backend, frontend, ecc...
		 */
		const appLink = setContext((_, { headers }) => {
			let newHeaders = decorateHeaders(headers);
			if (appContext) {
				newHeaders = {
					...headers,
					'X-Application-Context': appContext,
				};
			}
			return {
				headers: newHeaders,
			};
		});

		/***
		 * decorate every request with jwt (if available)
		 */
		const authLink = setContext((_, { headers }) => {
			const tokens = get();
			const newHeaders = {
				...headers,
				'x-api-key': defaultXAPIKey || '',
				'x-ref-host': getRefereerHost(),
			};
			if (tokens) {
				let token = tokens.token;
				if (headers && headers.___debug && headers.___debug.forceInvalidation) {
					token = 'invalidate!';
					delete newHeaders.___debug.forceInvalidation;
				}
				newHeaders.Authorization = `Bearer ${token}`;
			}
			return {
				headers: newHeaders,
			};
		});

		/***
		 * if invalid or expired token is detected, get a new token and retry request
		 */
		const retryLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
			const context = operation.getContext();
			if (gqlDetectAuthInvalid({ graphQLErrors, networkError })) {
				// refresh token and decorate same operation
				return fromPromise(
					refresh(refreshArgs)
						.then((res) => {
							return res;
						})
						.catch(() => {
							onInvalidAccess();
							forget();
						})
				)
					.filter((value) => {
						return Boolean(value);
					})
					.flatMap((tokens) => {
						set(tokens as AuthTokens);
						const headers = context.headers;
						operation.setContext({
							headers: {
								...headers,
								Authorization: `Bearer ${(tokens as AuthTokens).token}`,
								retryingAfterRefreshToken: true,
							},
						});
						// retry the request, returning the new observable
						return forward(operation);
					});
			}
		});
		/***
		 * if invalid token and already retried, then propagate invalid access
		 */
		const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
			const context = operation.getContext();
			if (gqlDetectAuthInvalid({ graphQLErrors, networkError })) {
				if (context.headers.retryingAfterRefreshToken) {
					// it's a definitive error!
					onInvalidAccess();
					forget();
				}
			} else {
				if (graphQLErrors && graphQLErrors.length > 0) {
					if (onErrors) {
						onErrors(
							graphQLErrors.map((err) => ({
								code: _get(err, 'extensions.code'),
								requestId: _get(err, 'extensions.data.requestId'),
								messages: [err.message],
								statusCode: _get(err, 'statusCode'),
							}))
						);
					}
				}
			}
		});

		const gqlClient = new ApolloClient({
			link: from([appLink, authLink, retryLink, errorLink, httpLink]),
			cache: new InMemoryCache({
				dataIdFromObject(responseObject) {
					if (responseObject.uniqueId) {
						return `${responseObject.__typename}:${responseObject.uniqueId}`;
					}
					return defaultDataIdFromObject(responseObject);
				},
			}),
		});

		return gqlClient;
	}, []);

	const getClient = useCallback((domain: string) => {
		if (!clientList[domain]) {
			clientList[domain] = createGraphQLClient(domain);
		}
		return clientList[domain];
	}, []);

	const createRestClient = useCallback(() => {
		const instance = axios.create({});
		instance.interceptors.request.use(
			(src) => {
				if (appContext) {
					if (!src.headers) src.headers = {};
					src.headers['X-Application-Context'] = appContext;
				}
				return src;
			},
			(error) => {
				return Promise.reject(error);
			}
		);
		/** manage invalidation and refresh token in REST */
		instance.interceptors.request.use(
			(src) => {
				if (src.headers && src.headers['has-jwt']) {
					const tokens = get();
					if (tokens) {
						const token = tokens.token;
						src.headers.Authorization = `Bearer ${token}`;
					}
				}
				if (src.headers && src.headers.___debugForceInvalidation) {
					src.headers.Authorization = `Bearer invalidate!`;
					delete src.headers.___debugForceInvalidation;
				}
				return src;
			},
			(error) => {
				return Promise.reject(error);
			}
		);
		instance.interceptors.response.use(
			function (response) {
				return response;
			},
			async function (error: AxiosError<any>) {
				const src = error.config;
				const refreshPath = AUTH_PATHS.refreshToken.replace(`{ver}`, `v${apiVersion}`);
				const preventRefreshToken = (src.headers || {}).preventRefreshToken;
				if (!preventRefreshToken && restDetectAuthInvalid(error)) {
					if (!(src as any)._retry && !src.url!.replace(`{ver}`, `v${apiVersion}`).includes(refreshPath)) {
						(src as any)._retry = true;
						try {
							const tokens = await refresh(refreshArgs);
							set(tokens as AuthTokens);
							return instance(src);
						} catch (_error) {
							// it's a definitive error!
							onInvalidAccess();
							// eslint-disable-next-line prefer-promise-reject-errors
							return Promise.reject({ response: { data: _error } });
						}
					}
				} else {
					const preventPropagateErrors = (src.headers || {}).preventPropagateErrors;
					if (onErrors && error && error.response && !preventPropagateErrors) {
						onErrors([
							{
								code: 'GENERIC',
								requestId: error.response.data.requestId,
								messages: error.response.data.messages,
								statusCode: error.response.data.statusCode,
							},
						]);
					}
				}
				return Promise.reject(error);
			}
		);
		return instance;
	}, []);

	const { domain, authDomain, authPaths, domains, refreshToken = {}, onContinueSession, onInvalidAccess } = props;
	const { override: customRefreshToken, headers: headersRefreshToken } = refreshToken;
	const { base, xAPIKey: defaultXAPIKey } = domains[domain];
	const { base: authBase, xAPIKey: authXAPIKey } = domains[authDomain || domain];
	const { set, get, forget } = useAuthToken();

	const gqlClient = useRef<ApolloClient<NormalizedCacheObject>>(createGraphQLClient(domain));
	clientList[domain] = gqlClient.current;

	/***
	 * if we found a JWT, try to load user-data
	 */
	useEffect(() => {
		const tokens = get();
		onContinueSession(tokens || undefined);
	}, []);

	// rest
	axios.defaults.baseURL = base;
	axios.defaults.headers.common['x-api-key'] = defaultXAPIKey || '';
	axios.defaults.headers.common['x-ref-host'] = getRefereerHost();
	const restClient = useRef<AxiosInstance>(createRestClient());

	const emit = {
		...props,
		gql: gqlClient.current,
		restClient: restClient.current,
		getClient,
	};

	const [defaultRefresh, refreshResponse] = useAuthRequest({
		action: 'refreshToken',
		domain: authDomain,
		xAPIKey: authXAPIKey,
		context: emit,
		paths: authPaths || {},
	});
	const refresh = customRefreshToken || defaultRefresh;
	const refreshArgs = {
		headers: headersRefreshToken || {},
	};

	if (!gqlClient.current) return null;
	return (
		<ApolloProvider client={gqlClient.current}>
			<DataContext.Provider value={emit}>{props.children}</DataContext.Provider>
		</ApolloProvider>
	);
};
